const document = window.document;

// shorthand

export const $ = <E extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as E;
export const $1 = <E extends Element = HTMLElement>(selectors: string) => document.querySelector<E>(selectors);
export const $$ = <E extends Element = HTMLElement>(selectors: string) => document.querySelectorAll<E>(selectors);

// eslint-disable-next-line no-sequences
export const $tap = <E>(obj: E, ...cbs: ((obj: E) => void)[]) => (cbs.forEach((cb) => cb(obj)), obj);
export const $n = (num: number, options?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat('en-US', options).format(num);

type ForEach<T> = { forEach: (cb: (e: T) => void) => void };
export const addClass = (elems: ForEach<Element | null | undefined>, className: string) =>
  elems.forEach((e) => e?.classList.add(className));
export const removeClass = (elems: ForEach<Element | null | undefined>, className: string) =>
  elems.forEach((e) => e?.classList.remove(className));

export const sleep = (msec: number) => new Promise<void>((resolve) => setTimeout(resolve, msec));

// events

export const $on = <K extends keyof HTMLElementEventMap, E extends Element = HTMLElement>(
  type: K,
  listeners: { readonly [selector: string]: (event: HTMLElementEventMap[K], currentTarget: E) => void },
) => {
  const listenerEntries = Object.entries(listeners);
  document.body.addEventListener(type, (event) => {
    for (const [selector, listener] of listenerEntries) {
      const currentTarget = (event.target as HTMLElement)?.closest<E>(selector);
      if (currentTarget) {
        listener(event, currentTarget);
        return;
      }
    }
  });
};

// elements

type TagProps<K extends keyof HTMLElementTagNameMap> = {
  readonly [key in keyof HTMLElementTagNameMap[K]]?: HTMLElementTagNameMap[K][key];
};

export const $e = <K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  { style, ...props }: Partial<Omit<TagProps<K>, 'style'> & { style?: Partial<CSSStyleDeclaration> }> = {},
  ...children: (string | Node)[]
) => {
  const element = document.createElement(tagName);
  Object.assign(element, props);
  Object.assign(element.style, style);
  element.append(...children);
  return element;
};

export const template = (id: string) => {
  const source = $(id).innerHTML;
  return (replacements: any) =>
    $e('div', { innerHTML: source.replace(/\{\{(\w+)\}\}/g, (_, key) => replacements[key]) });
};
